import { colors } from "@elit-hair/elit-tools";
import { SxProps, Theme, alpha } from "@mui/material";
import { blue, grey } from "@mui/material/colors";
import { SystemStyleObject } from "@mui/system";
import { GridRowProps, GridValidRowModel } from "@mui/x-data-grid";
import {
    DataGridPremium,
    GridApi,
    GridCellSelectionModel,
    GridRowId,
    GridRowIdGetter,
    GridRowModesModel,
    GridToolbarProps,
    gridClasses,
} from "@mui/x-data-grid-premium";
import { GridInitialStatePremium } from "@mui/x-data-grid-premium/models/gridStatePremium";
import {
    FooterPropsOverrides,
    GridCellParams,
    GridColDef,
    GridColumnVisibilityModel,
    GridEventListener,
    GridRowParams,
    GridRowSelectionModel,
    GridRowsProp,
    ToolbarPropsOverrides,
} from "@mui/x-data-grid-pro";
import { JSXElementConstructor, MutableRefObject, ReactNode } from "react";
import { TbChevronDown, TbChevronUp } from "react-icons/tb";

import useWindowDimensions from "@/hooks/useWindowDimensions";

import NoResultComponent from "./NoResults";
import NoRowsComponent from "./NoRows";
import { CommonTableFooter } from "./table/CommonTableFooter";

type CommonGridTableProps<T extends GridValidRowModel> = {
    api: MutableRefObject<GridApi>;
    rows: GridRowsProp;
    columns: GridColDef[];
    loading: boolean;
    checkboxSelection?: boolean;
    toolbar?: JSXElementConstructor<GridToolbarProps & ToolbarPropsOverrides>;
    getDetailPanelContent?: (params: GridRowParams) => ReactNode;
    columnHeaderHeight?: number;
    hideFooter?: boolean;
    rowSelectionModel?: GridRowSelectionModel;
    onRowSelectionModelChange?: (item: GridRowSelectionModel) => void;
    columnVisibilityModel?: GridColumnVisibilityModel;
    processRowUpdate?: (newRow: T, oldRow: T, params?: { rowId: GridRowId }) => T | Promise<T>;
    rowModesModel?: GridRowModesModel;
    allRows?: GridRowsProp;
    initialState?: GridInitialStatePremium | undefined;
    startElement?: JSXElementConstructor<{ allRows: GridRowsProp }>;
    onCellKeyDown?: GridEventListener<"cellKeyDown"> | undefined;
    onCellSelectionModelChange?: (selection: GridCellSelectionModel) => void;
    detailPanelExpandedRowIds?: GridRowId[];
    onDetailPanelExpandedRowIdsChange?: (ids: GridRowId[]) => void;
    disableColumnFilter?: boolean;
    disableColumnPinning?: boolean;
    disableColumnReorder?: boolean;
    disableColumnMenu?: boolean;
    getRowId?: GridRowIdGetter<T>;
    disableRowSelectionOnClick?: boolean;
    isRowSelectable?: (params: GridRowParams) => boolean;
    /** Override per-cell editability. When omitted, falls back to `preventCellEdit || colDef.editable`. */
    isCellEditable?: (params: GridCellParams) => boolean;
    preventCellEdit?: boolean;
    CustomGridRow?: React.ComponentType<GridRowProps>;
    /** When true, grid height grows with rows (omit fixed height/`flex` fill from `gridSx`). */
    autoHeight?: boolean;
    /** Merged into DataGrid `sx` (e.g. height for internal scroll + sticky header). */
    gridSx?: SxProps<Theme> | ((theme: Theme) => SxProps<Theme>);
};

const getTogglableColumns = (columns: GridColDef[]) => {
    return columns.filter((column) => !["index", "actions"].includes(column.field)).map((column) => column.field);
};

function customCheckbox(theme: Theme) {
    return {
        ["& .MuiDataGrid-menuIconButton"]: {
            padding: 0,
        },
        ["& .MuiDataGrid-columnHeaderTitleContainerContent"]: {
            width: "100%",
        },
        ["& .checkbox-header .MuiDataGrid-columnHeaderTitleContainerContent .MuiButtonBase-root"]: {
            margin: "auto !important",
        },
        ["& .MuiDataGrid-columnHeader"]: {
            padding: 0,
            paddingLeft: "3.5px",
            paddingRight: "3.5px",
        },
        ["& .MuiDataGrid-filler--pinnedLeft"]: {
            borderRightWidth: "0px !important",
        },
        ["& .MuiDataGrid-filler--pinnedRight"]: {
            borderLeftWidth: "0px !important",
        },
        ["& .MuiDataGrid-actionsCell"]: {
            columnGap: "0px",
            rowGap: "0px",
            gap: "0px",
        },
        ["& .MuiDataGrid-row"]: {
            ["& .MuiDataGrid-cell"]: {
                backgroundColor: "inherit",
            },
            "&:hover": {
                backgroundColor: grey[100],
                "@media (hover: none)": {
                    backgroundColor: "transparent",
                },
            },
            "&.Mui-selected": {
                backgroundColor: `${blue[50]} !important`,
            },
        },
        ["& .MuiDataGrid-detailPanelToggleCell"]: {
            display: "grid !important",
            height: "100%",
            width: "100%",
            placeContent: "center",
            padding: 0,
        },
        [`& .MuiDataGrid-row.even`]: {
            backgroundColor: grey[50],
            ["& .MuiDataGrid-cell"]: {
                backgroundColor: "inherit",
            },
            "&:hover": {
                backgroundColor: grey[100],
                "@media (hover: none)": {
                    backgroundColor: "transparent",
                },
            },
            "&.Mui-selected": {
                backgroundColor: blue[50],
            },
        },
        "& .MuiCheckbox-root svg": {
            width: 16,
            height: 16,
            backgroundColor: "transparent",
            border: "1px solid #d9d9d9",
            borderRadius: 2,
        },
        "& .MuiCheckbox-root svg path": {
            display: "none",
        },
        "& .MuiCheckbox-root.Mui-checked:not(.MuiCheckbox-indeterminate) svg": {
            backgroundColor: colors.elitBlue,
            borderColor: colors.elitBlue,
        },
        "& .MuiCheckbox-root.Mui-checked .MuiIconButton-label:after": {
            position: "absolute",
            display: "table",
            border: "2px solid #fff",
            borderTop: 0,
            borderLeft: 0,
            transform: "rotate(45deg) translate(-50%,-50%)",
            opacity: 1,
            transition: "all .2s cubic-bezier(.12,.4,.29,1.46) .1s",
            content: '""',
            top: "50%",
            left: "39%",
            width: 5.71428571,
            height: 9.14285714,
        },
        "& .MuiCheckbox-root.MuiCheckbox-indeterminate .MuiIconButton-label:after": {
            width: 8,
            height: 8,
            backgroundColor: "#1890ff",
            transform: "none",
            top: "39%",
            border: 0,
        },
        [`& .${gridClasses.cell}.Mui-selected`]: {
            borderColor: alpha(theme.palette.primary.main, 0.1),
            cursor: "ns-resize",
            backgroundColor: `${blue[50]} !important`,
        },
        [`& .${gridClasses.cell}.Mui-selected.${gridClasses["cell--rangeTop"]}`]: {
            borderTopColor: blue[200],
            borderTopWidth: "2px",
        },
        [`& .${gridClasses.cell}.Mui-selected.${gridClasses["cell--rangeBottom"]}`]: {
            borderBottomColor: blue[200],
        },
        [`& .${gridClasses.cell}.Mui-selected.${gridClasses["cell--rangeLeft"]}`]: {
            borderLeftColor: blue[200],
        },
        [`& .${gridClasses.cell}.Mui-selected.${gridClasses["cell--rangeRight"]}`]: {
            borderRightColor: blue[200],
        },
    };
}

export default function CommonGridTable<T extends GridValidRowModel>({
    api,
    rows,
    columns,
    loading,
    checkboxSelection,
    toolbar,
    getDetailPanelContent,
    columnHeaderHeight,
    hideFooter,
    onRowSelectionModelChange,
    rowSelectionModel,
    columnVisibilityModel,
    processRowUpdate,
    rowModesModel,
    allRows,
    initialState,
    startElement,
    onCellKeyDown,
    onCellSelectionModelChange,
    detailPanelExpandedRowIds,
    onDetailPanelExpandedRowIdsChange,
    disableColumnFilter,
    disableColumnPinning,
    disableColumnReorder,
    disableColumnMenu,
    getRowId,
    disableRowSelectionOnClick,
    isRowSelectable,
    isCellEditable,
    preventCellEdit,
    CustomGridRow,
    autoHeight,
    gridSx,
}: CommonGridTableProps<T>) {
    const { size } = useWindowDimensions();

    return (
        <DataGridPremium
            autoHeight={autoHeight || false}
            cellSelection
            columnVisibilityModel={columnVisibilityModel || undefined}
            disableVirtualization
            disableDensitySelector
            disableAggregation
            disableRowSelectionOnClick={disableRowSelectionOnClick}
            disableRowGrouping
            editMode="row"
            density={size.md ? "standard" : "compact"}
            columnHeaderHeight={columnHeaderHeight || 50}
            getDetailPanelContent={getDetailPanelContent}
            getDetailPanelHeight={() => "auto"}
            isCellEditable={(params) => {
                if (isCellEditable) return isCellEditable(params);
                return preventCellEdit || params.colDef.editable || false;
            }}
            sx={(theme) => {
                const fromGrid = (gridSx ? (typeof gridSx === "function" ? gridSx(theme) : gridSx) : undefined) as
                    | SystemStyleObject<Theme>
                    | undefined;
                return {
                    ["& .MuiDataGrid-columnHeaderTitle"]: {
                        fontWeight: "bold",
                        padding: 0,
                    },
                    "& .MuiDataGrid-cell": {
                        padding: 0,
                        borderColor: "transparent",
                        borderStyle: "solid",
                    },
                    "& .MuiDataGrid-iconSeparator": {
                        display: "none",
                    },
                    "& .MuiDataGrid-columnsContainer, .MuiDataGrid-cell": {
                        borderBottom: "1px solid green",
                        ...theme.applyStyles("light", {
                            borderBottomColor: "#f0f0f0",
                        }),
                    },

                    ...customCheckbox(theme),
                    ...theme.applyStyles("light", {
                        color: "rgba(0,0,0,.85)",
                    }),
                    ...fromGrid,
                };
            }}
            getRowId={getRowId}
            isRowSelectable={isRowSelectable}
            getRowClassName={(params) => {
                let cls =
                    params.indexRelativeToCurrentPage % 2 === 0 ? "even text-sm bg-white" : "odd text-sm bg-white";
                if (params.row.cancelled) cls += " !bg-red-50 !text-red-800 !line-through ";

                return cls;
            }}
            // classes={{
            //     [gridClasses.]
            // }}
            getRowHeight={() => "auto"}
            apiRef={api}
            checkboxSelection={checkboxSelection}
            columns={columns}
            slots={{
                toolbar,
                noRowsOverlay: NoRowsComponent,
                noResultsOverlay: NoResultComponent,
                detailPanelExpandIcon: TbChevronDown,
                detailPanelCollapseIcon: TbChevronUp,
                footer: CommonTableFooter,
                row: CustomGridRow || undefined,
            }}
            rows={rows as readonly T[]}
            loading={loading}
            hideFooterRowCount={false}
            onCellKeyDown={onCellKeyDown}
            slotProps={{
                columnsManagement: {
                    disableShowHideToggle: true,
                    getTogglableColumns,
                },
                loadingOverlay: {
                    variant: "linear-progress",
                    noRowsVariant: "skeleton",
                },
                toolbar: {
                    showQuickFilter: true,
                },
                footer: {
                    startElement,
                    rows,
                    allRows,
                } as FooterPropsOverrides,
            }}
            hideFooter={hideFooter || false}
            onRowSelectionModelChange={onRowSelectionModelChange || undefined}
            rowSelectionModel={rowSelectionModel || undefined}
            processRowUpdate={processRowUpdate || undefined}
            rowModesModel={rowModesModel || undefined}
            initialState={initialState}
            onCellSelectionModelChange={onCellSelectionModelChange}
            detailPanelExpandedRowIds={detailPanelExpandedRowIds}
            onDetailPanelExpandedRowIdsChange={onDetailPanelExpandedRowIdsChange}
            disableColumnFilter={disableColumnFilter}
            disableColumnPinning={disableColumnPinning}
            disableColumnReorder={disableColumnReorder}
            disableColumnMenu={disableColumnMenu}
        />
    );
}
